import {
  CampoRegularizacao,
  FormularioRegularizacaoResponse
} from 'src/api/recebedor-pendencia-bacen'

export type GrupoCampoId =
  | 'empresa'
  | 'enderecoEmpresa'
  | 'representante'
  | 'enderecoRepresentante'
  | 'pessoais'
  | 'endereco'

export type GrupoCampos = {
  id: GrupoCampoId
  titulo: string
  campos: CampoRegularizacao[]
}

const CAMPOS_EMPRESA = new Set([
  'company_name',
  'trading_name',
  'annual_revenue',
  'corporation_type',
  'founding_date'
])

const CAMPOS_PESSOAIS = new Set([
  'name',
  'birthdate',
  'monthly_income',
  'professional_occupation',
  'document',
  'type'
])

const CAMPOS_PJ_MARCADORES = [
  'main_address',
  'managing_partners',
  'company_name',
  'trading_name',
  'annual_revenue',
  'corporation_type',
  'founding_date'
]

const ORDEM_PJ: GrupoCampoId[] = [
  'empresa',
  'enderecoEmpresa',
  'representante',
  'enderecoRepresentante'
]

const ORDEM_PF: GrupoCampoId[] = ['pessoais', 'endereco']

const TITULOS: Record<GrupoCampoId, string> = {
  empresa: 'Informações da Empresa',
  enderecoEmpresa: 'Endereço da Empresa',
  representante: 'Informações do Representante',
  enderecoRepresentante: 'Endereço do Representante',
  pessoais: 'Dados pessoais',
  endereco: 'Endereço'
}

export function camposExigemLayoutPj(campos: CampoRegularizacao[]) {
  return campos.some(campo =>
    CAMPOS_PJ_MARCADORES.some(marcador => campo.campo.startsWith(marcador))
  )
}

export function inferirTipoPessoa(
  formulario: FormularioRegularizacaoResponse
): 'PF' | 'PJ' | null {
  if (formulario.tipoPessoa === 'PF' || formulario.tipoPessoa === 'PJ') {
    return formulario.tipoPessoa
  }

  const campos = formulario.campos || []
  if (camposExigemLayoutPj(campos)) {
    return 'PJ'
  }

  const digitos = (formulario.contaBancaria?.cpfCnpj || '').replace(/\D/g, '')
  if (digitos.length === 11) return 'PF'
  if (digitos.length >= 12) return 'PJ'
  return null
}

export function tituloTipoRecebedor(
  tipo?: string | null,
  subtipo?: string | null
) {
  if (tipo === 'M') return 'Médico'
  if (tipo === 'ME') return 'Membro de equipe'
  if (tipo === 'P' && subtipo === 'Hospital') return 'Hospital/Clínica'
  if (tipo === 'P' && subtipo === 'OPME') return 'Fornecedor de OPME'
  if (tipo === 'P') return 'Parceiro'
  if (tipo === 'FA') return 'Fornecedor de anestesia'
  if (tipo === 'FS') return 'Fornecedor de bolsa de sangue'
  if (tipo === 'SYN') return 'Syn'
  return tipo || ''
}

export function rotuloTipoPessoa(tipo?: string | null) {
  if (tipo === 'PF') return 'Pessoa física'
  if (tipo === 'PJ') return 'Pessoa jurídica'
  return ''
}

function classificarCampo(
  campo: CampoRegularizacao,
  ehPj: boolean
): GrupoCampoId {
  const chave = campo.campo

  if (chave.startsWith('main_address.')) return 'enderecoEmpresa'
  if (chave.startsWith('managing_partners[0].address.')) {
    return 'enderecoRepresentante'
  }
  if (chave.startsWith('managing_partners[0].')) return 'representante'
  if (chave.startsWith('address.')) return 'endereco'
  if (CAMPOS_EMPRESA.has(chave)) return 'empresa'
  if (CAMPOS_PESSOAIS.has(chave)) return ehPj ? 'empresa' : 'pessoais'
  return ehPj ? 'empresa' : 'pessoais'
}

export function agruparCampos(
  campos: CampoRegularizacao[],
  ehPj: boolean
): GrupoCampos[] {
  const grupos = new Map<GrupoCampoId, CampoRegularizacao[]>()

  campos.forEach(campo => {
    const id = classificarCampo(campo, ehPj)
    const atual = grupos.get(id) || []
    atual.push(campo)
    grupos.set(id, atual)
  })

  const preferida = ehPj ? ORDEM_PJ : ORDEM_PF
  const complemento = (ehPj ? ORDEM_PF : ORDEM_PJ).filter(
    id => !preferida.includes(id)
  )
  const ordem = [...preferida, ...complemento]
  return ordem
    .filter(id => (grupos.get(id) || []).length > 0)
    .map(id => ({
      id,
      titulo: TITULOS[id],
      campos: ordenarCamposGrupo(id, grupos.get(id) || [])
    }))
}

const ORDEM_ENDERECO = [
  'zip_code',
  'state',
  'city',
  'street',
  'neighborhood',
  'street_number',
  'complementary',
  'reference_point'
]

const GRUPOS_ENDERECO = new Set<GrupoCampoId>([
  'endereco',
  'enderecoEmpresa',
  'enderecoRepresentante'
])

function ordenarCamposGrupo(id: GrupoCampoId, campos: CampoRegularizacao[]) {
  if (!GRUPOS_ENDERECO.has(id)) return campos

  return [...campos].sort((a, b) => {
    const sa = a.campo.split('.').pop() || a.campo
    const sb = b.campo.split('.').pop() || b.campo
    const ia = ORDEM_ENDERECO.indexOf(sa)
    const ib = ORDEM_ENDERECO.indexOf(sb)
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib)
  })
}

/**
 * react-hook-form v6 trata "." e "[0]" como path aninhado.
 * Achata a chave Pagar.me para o form tratar cada campo como folha.
 */
export function chaveCampoFormulario(campo: string) {
  return campo.replace(/\[(\d+)\]/g, '_$1').replace(/\./g, '__')
}

const MENSAGEM_SITE_URL =
  'Informe uma URL válida, começando com http:// ou https://, sem barra, símbolo ou espaço no final.'

export const PREFIXOS_SITE_URL = ['https://', 'http://'] as const

export type PrefixoSiteUrl = typeof PREFIXOS_SITE_URL[number]

export function mensagemSiteUrlInvalida() {
  return MENSAGEM_SITE_URL
}

export function separarSiteUrl(
  value: string
): {
  prefixo: PrefixoSiteUrl
  resto: string
} {
  const trimmed = String(value || '').trim()
  const lower = trimmed.toLowerCase()
  if (lower.startsWith('https://')) {
    return { prefixo: 'https://', resto: trimmed.slice('https://'.length) }
  }
  if (lower.startsWith('http://')) {
    return { prefixo: 'http://', resto: trimmed.slice('http://'.length) }
  }
  return { prefixo: 'https://', resto: trimmed }
}

export function montarSiteUrl(prefixo: string, resto: string) {
  const texto = String(resto || '')
  const lower = texto.trim().toLowerCase()
  if (lower.startsWith('https://') || lower.startsWith('http://')) {
    const partes = separarSiteUrl(texto)
    return `${partes.prefixo}${partes.resto}`
  }
  const prefixoFinal: PrefixoSiteUrl =
    prefixo === 'http://' ? 'http://' : 'https://'
  return `${prefixoFinal}${texto.trim()}`
}

export function isSiteUrlValida(value: string) {
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  if (!trimmed) return false
  const lower = trimmed.toLowerCase()
  if (!lower.startsWith('https://') && !lower.startsWith('http://')) {
    return false
  }

  const ultimo = trimmed.charAt(trimmed.length - 1)
  if (!/[a-zA-Z0-9]/.test(ultimo)) return false

  try {
    const url = new URL(trimmed)
    return url.protocol === 'https:' || url.protocol === 'http:'
  } catch {
    return false
  }
}

const ROTULOS_CURTOS: Record<string, string> = {
  zip_code: 'CEP',
  state: 'UF',
  city: 'Cidade',
  street: 'Endereço',
  neighborhood: 'Bairro',
  street_number: 'Número',
  complementary: 'Complemento',
  reference_point: 'Ponto de referência',
  birthdate: 'Data de nascimento',
  monthly_income: 'Renda mensal',
  professional_occupation: 'Ocupação',
  email: 'E-mail',
  name: 'Nome',
  phone_numbers: 'Telefone'
}

/**
 * A seção do formulário já identifica empresa/representante.
 * Rótulos longos do catálogo estouram o outlined do MUI nas colunas estreitas.
 */
export function rotuloCampo(campo: CampoRegularizacao) {
  const sufixo = campo.campo.split('.').pop() || campo.campo
  if (sufixo === 'document' && campo.campo.includes('managing_partners')) {
    return 'CPF'
  }
  const curto = ROTULOS_CURTOS[sufixo]
  if (curto && campo.rotulo.length > curto.length + 4) return curto
  return campo.rotulo
}

export function larguraCampo(campo: CampoRegularizacao) {
  const sufixo = campo.campo.split('.').pop() || campo.campo

  if (campo.tipo === 'confirmacao') return { xs: 12 as const }
  if (sufixo === 'zip_code') return { xs: 12 as const, sm: 4 as const }
  if (sufixo === 'state') return { xs: 12 as const, sm: 3 as const }
  if (sufixo === 'city') return { xs: 12 as const, sm: 5 as const }
  if (sufixo === 'street' || sufixo === 'neighborhood') {
    return { xs: 12 as const }
  }
  if (sufixo === 'street_number') return { xs: 12 as const, sm: 4 as const }
  if (sufixo === 'complementary') return { xs: 12 as const, sm: 4 as const }
  if (sufixo === 'reference_point') return { xs: 12 as const, sm: 4 as const }

  return { xs: 12 as const, md: 6 as const }
}
